"use client"

import Link from "next/link"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { ArrowLeft } from "lucide-react"
import { useAuth } from "@/hooks/useAuth"
import { useToast } from "@/components/ui/use-toast"

// Definindo uma interface para o erro
interface AuthError {
  message?: string;
  code?: string;
  statusCode?: number;
}

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isEmailSent, setIsEmailSent] = useState(false)
  const { resetPassword } = useAuth()
  const { toast } = useToast()

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email) {
      toast({
        title: "Erro de validação",
        description: "Por favor, informe seu email.",
        variant: "destructive"
      })
      return
    }
    
    try {
      setIsLoading(true)
      const { error } = await resetPassword(email)
      
      if (error) {
        throw error
      }
      
      setIsEmailSent(true)
      toast({
        title: "Email enviado",
        description: "Verifique seu email para redefinir sua senha.",
      })
    } catch (error: unknown) {
      const authError = error as AuthError;
      toast({
        title: "Erro ao enviar email",
        description: authError.message || "Ocorreu um erro ao tentar enviar o email. Tente novamente.",
        variant: "destructive"
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100 px-4 py-12">
      <Card className="w-full max-w-md">
        <div className="absolute left-4 top-4">
          <Button variant="ghost" size="icon" onClick={() => window.history.back()} aria-label="Voltar">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </div>
        <CardHeader className="space-y-1 text-center">
          <CardTitle className="text-2xl font-bold">Esqueceu sua senha?</CardTitle>
          <CardDescription>
            {isEmailSent
              ? "Enviamos um link para redefinir sua senha para o seu email."
              : "Informe seu email e enviaremos um link para redefinir sua senha."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isEmailSent ? (
            <div className="bg-green-50 border border-green-200 rounded-md p-4 text-center">
              <p className="text-sm text-green-800">
                Verifique seu email para redefinir sua senha. O link enviado expira em 1 hora.
              </p>
            </div>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input 
                  id="email" 
                  type="email" 
                  placeholder="seu@email.com" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <Button 
                className="w-full bg-black hover:bg-gray-800" 
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? "Enviando..." : "Enviar link de redefinição"}
              </Button>
            </form>
          )}
        </CardContent>
        <CardFooter className="flex justify-center">
          <div className="text-sm text-muted-foreground">
            Lembrou sua senha?{" "}
            <Link href="/auth/login" className="text-primary hover:underline">
              Voltar para o login
            </Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  )
}
